/**
 * Service for the wiberry Sync Agent's UI-management surface (#3071).
 *
 * This is the ordinary session-cookie-authenticated browser side of the
 * wiberry epic: generate/cancel an enrolment token, list outstanding tokens,
 * list connected agents, revoke an agent, list recent sync runs. It is a
 * COMPLETELY DIFFERENT surface from the 7 agent-bearer-facing protocol
 * endpoints under `/api/v1/integrations/agent/*` (`POST /enroll`, `POST
 * /token`, `GET /schema`, `POST /runs`, ... — see
 * `docs/architecture/wiberry-sync-agent-protocol.md`) — the browser never
 * holds an agent access/refresh token and never calls those. `18-3068`'s BFF
 * bearer-passthrough router (`bff-service/src/api/integrations_agent.py`)
 * only forwards those 7 named agent-facing sub-paths; the UI-management
 * sub-paths below (`/enrolment-tokens*`, `/agents*`, and — since #3120 —
 * `GET /runs`) are NOT among them, so they fall through to the BFF's generic
 * session-cookie catch-all (`proxy.py`'s `/api/v1/{path:path}`), same as
 * `integrationAgentService.ts`'s (#1936) `/imports` call — no BFF-side code
 * needed for this file either.
 *
 * **Real path/shapes confirmed against `16-3067a` (PR #3109,
 * `backend/src/api/v1/endpoints/wiberry_agent.py` +
 * `backend/src/schemas/wiberry_agent.py`)** — this replaces #3071's
 * originally-hypothesised `/v1/integrations/wiberry/*` path, which turned out
 * not to be what shipped. The real UI-management routes share the SAME
 * `/integrations/agent` prefix as #1936's `/imports` and #3068's
 * agent-facing routes, distinguished by non-colliding named sub-paths
 * (`/enrolment-tokens*`, `/agents*` vs `/imports`, `/enroll`, `/token`,
 * `/schema`, `/runs*`) — plus, for `GET /runs` alone, by METHOD rather than
 * by path (see `listRuns` below).
 *
 * **Every route this file calls is live** as of #3120: pass 1
 * (tokens/agents) against #3067a, and the runs feed against #3120's
 * `list_runs`, which is the UI-facing handler #3067b's run-lifecycle leg
 * never shipped.
 */
import api from './api';

/** Wire shape of one enrolment-token row (`EnrolmentTokenResponse` on the
 * backend). `enrolment_token` (the plaintext) is present ONLY on the
 * `POST /enrolment-tokens` response — always `null` on the list read, since
 * only the token's hash is ever persisted (FR-016.1). */

/** Response of generating a new enrolment token — the ONE response that ever
 * carries the plaintext `enrolment_token`. Callers must not persist it
 * beyond the shown-once dialog's local state. `install_command` is NOT part
 * of the backend's wire response (`EnrolmentTokenResponse` carries no such
 * field) — it is templated client-side, in this service, from the literal
 * CLI form the architecture doc documents (`hofsync-agent enroll --token
 * <token>`, `docs/architecture/wiberry-sync-agent.md`). */

/** Wire shape of one connected-agent row (`AgentResponse` on the backend).
 * This is the #3067a identity/config slice ONLY — it carries no sync-status
 * field (no "last successful sync"). That data lives on the runs feed, which
 * `WiberryAgentPanel.tsx` joins client-side (latest `merged` run's
 * `finished_at` per `agent_id`); since #3120 that join reads real runs. */

export interface WiberryRunError {
  code: string;
  /** Protocol §3.7's engineer-facing diagnostic string. Optional on the wire
   * (`RunErrorResponse.message`) because the UDB `error` JSONB is written by
   * three independent services and only `code` is guaranteed by all of them —
   * and NEVER rendered: `WiberryRunsList` maps `code` to plain-German copy
   * instead (FR-016.7). */
  message?: string | null;
}

export type WiberryRunMode = 'full' | 'incremental' | 'reconcile';
export type WiberryRunOutcome = 'running' | 'merged' | 'failed' | 'expired';

/** One row of the recent-runs table — backend's `RunSummaryResponse` (#3120),
 * a deliberate PROJECTION of the UDB run row rather than a passthrough of it.
 * Two projections are worth knowing when reading this type:
 *
 * - `outcome` is NOT UDB's `status`. The four in-flight statuses
 *   (`open`/`queued`/`compiling`/`merging`) collapse server-side into one
 *   `running` — the pipeline's internal stage is not a farmer-facing fact,
 *   which is why this union has no member for it.
 * - `rows_ingested`/`erntejahre` are derived from the run's `report`/
 *   `coverage` JSONB, not columns. `null` (rendered "—") means "not reported
 *   yet", never zero.
 */
export interface WiberryRun {
  id: string;
  /** Verified agent identity persisted at run creation (#3128). `null` for
   * runs created before UDB migration 0070 — callers must tolerate it. */
  agent_id: string | null;
  started_at: string;
  finished_at: string | null;
  mode: WiberryRunMode;
  outcome: WiberryRunOutcome;
  rows_ingested: number | null;
  erntejahre: string[] | null;
  /** Structured cause of a `failed` run. The frontend maps `code` to
   * plain-German copy (see `WiberryRunsList.tsx`) rather than surfacing
   * `message`, which protocol §3.7 leaves as an engineer-facing diagnostic
   * string, not pre-translated prose. */
  error: WiberryRunError | null;
}

export interface WiberryRunsResponse {
  runs: WiberryRun[];
}

/** `GET/DELETE .../enrolment-tokens/{id}` and `.../agents/{id}` error body —
 * a plain FastAPI `{"detail": "<code>"}` (NOT the agent-facing routes' flat
 * `{"error": ...}` shape — these are the session-cookie UI-management
 * routes, which raise ordinary `HTTPException`s). */

/** S9 — the settings page's heartbeat badge. Server-judged, not agent-claimed. */
export type IntegrationLivenessState = 'online' | 'offline' | 'never_seen' | 'revoked';

export interface IntegrationLiveness {
  state: IntegrationLivenessState;
  last_seen_at: string | null;
  seconds_since_last_seen: number | null;
  overdue_by_seconds: number;
  stall_window_seconds: number | null;
}

export interface IntegrationLastSync {
  run_id: string;
  outcome: WiberryRunOutcome;
  mode: string;
  started_at: string;
  finished_at: string | null;
  rows_ingested: number | null;
  /** Compressed bytes across the wire, summed from the run's per-batch report. */
  bytes_synced: number | null;
}

/**
 * One provider, ONE integration — a single object, not a list, because a tenant
 * has at most one wiberry connection.
 *
 * Read against `oauth_clients` (the device-flow pairing era), NOT
 * `integration_agents` (v1 `/enroll`). The shipped agents list reads the latter,
 * which is why a device-flow pairing shows up here and not there.
 */
/** One ERROR line the AGENT uploaded, for the last sync only. */
export interface AgentReportedError {
  logged_at: string;
  event: string;
  message: string | null;
}

export interface IntegrationStatus {
  source: string;
  connected: boolean;
  client_id: string | null;
  name: string | null;
  platform: string | null;
  agent_version: string | null;
  paired_at: string | null;
  cadence_seconds: number | null;
  liveness: IntegrationLiveness | null;
  last_sync: IntegrationLastSync | null;
  /** Empty means the last sync reported none — a positive statement. */
  last_sync_errors: AgentReportedError[];
  /** `false` when the log read itself failed: we could not check, not "clean". */
  last_sync_errors_available: boolean;
}

export const wiberryAgentService = {

  /** List recent sync runs across all of the tenant's agents, most-recent
   * first.
   *
   * **LIVE since #3120.** #3071 shipped this call fixture-tested against a
   * hypothesised path with no handler behind it; #3067b's run-lifecycle leg
   * then landed only the protocol's four agent-facing routes and no
   * UI-facing list, so #3120 added the handler
   * (`backend/src/api/v1/endpoints/wiberry_agent.py::list_runs`) — at the
   * path this file had already guessed. The guess turned out right here, so
   * unlike pass 1 (where `/v1/integrations/wiberry/*` had to become
   * `/v1/integrations/agent/*`) nothing moved.
   *
   * `GET` on the BARE `/runs` is load-bearing, not incidental: the BFF's
   * bearer-passthrough router (#3068) owns `GET /runs/{run_id}`, so any
   * extra literal segment (`/runs/recent`) would be captured there as a
   * run_id and forwarded with no session → 401. A bare `/runs` matches none
   * of that router's 7 named routes (its own `/runs` is POST-only), so
   * Starlette's "a full method match beats an earlier path-only partial
   * match" rule lands it on the BFF's session-cookie catch-all, same as
   * `/agents` and `/enrolment-tokens` above.
   *
   * Server-side defaults: `source=wiberry`, `limit=20` — both are query
   * params on the route, deliberately not sent from here so the panel has
   * one fewer contract to keep in sync.
   */
  listRuns: async (): Promise<WiberryRunsResponse> => {
    const response = await api.get<WiberryRunsResponse>('/v1/integrations/agent/runs');
    return response.data;
  },

  /** S9 — this tenant's single wiberry integration: heartbeat, last sync, volume. */
  getStatus: async (source = 'wiberry'): Promise<IntegrationStatus> => {
    const response = await api.get<IntegrationStatus>('/v1/integrations/status', { params: { source } });
    return response.data;
  },
};
